import { useState, useCallback } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { useAuth } from '@/hooks/use-auth';

interface NavItem {
  id: string;
  path: string;
  label: string;
  adminOnly: boolean;
}

// Primary nav — top of the panel. Labels ALL-CAPS, no "THE" prefix
// (2026-05-15 user direction).
const PRIMARY_NAV: NavItem[] = [
  { id: 'cockpit', path: '/cockpit', label: 'COCKPIT', adminOnly: false },
  // Solver hidden 2026-05-15 per user direction — page + components stay in
  // place at `apps/web/src/routes/solver.tsx` + `apps/web/src/components/solver/*`.
  // Re-enable by uncommenting this row and the solverRoute block in App.tsx.
  // { id: 'solver', path: '/solver', label: 'SOLVER', adminOnly: false },
  { id: 'builder', path: '/builder', label: 'BUILDER', adminOnly: false },
  { id: 'pipeline', path: '/pipeline', label: 'PIPELINE', adminOnly: false },
  { id: 'shares', path: '/shares', label: 'LINKS', adminOnly: false },
];

// Secondary nav — pinned to the bottom of the panel (just above the user
// card) via `mt-auto` on its render block (2026-05-15 user direction:
// "Admin and settings should be moved to the bottom of the panel").
const SECONDARY_NAV: NavItem[] = [
  { id: 'settings', path: '/settings', label: 'Settings', adminOnly: false },
  { id: 'admin', path: '/admin', label: 'Admin', adminOnly: true },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { user, signOut, isAdmin } = useAuth();

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') return currentPath === '/';
      return currentPath.startsWith(path);
    },
    [currentPath]
  );

  const initials = user?.email?.slice(0, 2).toUpperCase() || 'AA';

  const visible = useCallback(
    (item: NavItem) => {
      if (item.adminOnly && !isAdmin) return false;
      return true;
    },
    [isAdmin]
  );

  const renderNavItem = useCallback(
    (item: NavItem) => {
      const active = isActive(item.path);
      return (
        <button
          key={item.id}
          onClick={() => navigate({ to: item.path })}
          className={clsx(
            'flex items-center gap-2 rounded-[2px] border font-mono text-sm cursor-pointer text-left transition-all duration-100',
            collapsed ? 'p-2 justify-center' : 'py-2 px-3',
            active
              ? 'bg-accent-faint border-accent/8 text-accent font-semibold'
              : 'bg-transparent border-transparent text-text-dim font-normal hover:text-text-muted'
          )}
          style={{ letterSpacing: '0.3px' }}
        >
          {active && (
            <span className="w-0.5 h-3.5 bg-accent rounded-[1px] shrink-0 shadow-[0_0_4px_var(--color-accent)]" />
          )}
          {!collapsed && item.label}
        </button>
      );
    },
    [collapsed, isActive, navigate]
  );

  return (
    <div
      className={clsx(
        'h-full bg-void border-r border-panel-border flex flex-col shrink-0 transition-[width] duration-200',
        collapsed ? 'w-11' : 'w-44 2xl:w-48'
      )}
    >
      {/* Logo */}
      <div
        className={clsx(
          'flex items-center gap-2.5 border-b border-panel-border min-h-12',
          collapsed ? 'px-2.5 py-3 justify-center' : 'px-4 py-3'
        )}
      >
        <svg
          width={collapsed ? 14 : 16}
          height={collapsed ? 14 : 18}
          viewBox={`0 0 ${collapsed ? 14 : 16} ${collapsed ? 14 : 18}`}
          className="shrink-0"
        >
          <rect
            x="0"
            y="0"
            width="5"
            height={collapsed ? 14 : 18}
            rx={collapsed ? 2 : 3}
            fill="var(--color-accent)"
          />
          <rect
            x="8"
            y="0"
            width="5"
            height={collapsed ? 14 : 18}
            rx={collapsed ? 2 : 3}
            fill="var(--color-accent)"
          />
        </svg>
        {!collapsed && (
          <div>
            <div className="text-sm font-bold text-text-bright font-body tracking-[1.5px]">
              ARCANUM
            </div>
            <div className="text-micro text-accent font-mono tracking-[2px] mt-[-1px]">
              AI UNDERWRITING
            </div>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <div className="px-3 pt-2 pb-px">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="bg-transparent border-none text-text-dim cursor-pointer text-sm px-1 py-0.5 font-mono"
          data-testid="collapse-toggle"
        >
          {collapsed ? '\u25B8' : '\u25C2'}
        </button>
      </div>

      {/* Nav label */}
      {!collapsed && (
        <div className="px-4 pb-2 pt-px text-xs text-text-muted font-mono tracking-[2.5px] font-medium">
          NAVIGATION
        </div>
      )}

      {/* Primary nav items (top) */}
      <div className="flex flex-col gap-0.5 px-3">
        {PRIMARY_NAV.filter(visible).map(renderNavItem)}
      </div>

      {/* Secondary nav (Settings/Admin) — pinned to the bottom of the nav
       * panel via `mt-auto` (consumes the flex gap so the user card follows
       * directly beneath). 2026-05-15 user direction. */}
      <div className="mt-auto flex flex-col gap-0.5 px-3">
        {SECONDARY_NAV.filter(visible).map(renderNavItem)}
      </div>

      {/* User section — directly below SECONDARY_NAV at the very bottom */}
      <div className="p-3.5 border-t border-panel-border mt-2">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-[2px] bg-accent-dim border border-accent/9 flex items-center justify-center text-xs text-accent font-bold font-mono shrink-0">
            {initials}
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-sm font-medium text-text font-body truncate">
                {user?.email?.split('@')[0] || 'user'}
              </div>
              <div className="text-micro text-text-dim font-mono truncate">
                {user?.email || ''}
              </div>
            </div>
          )}
        </div>
        {!collapsed && (
          <button
            onClick={signOut}
            className="mt-2.5 text-xs text-text-dim font-mono cursor-pointer bg-transparent border-none hover:text-text-muted"
          >
            Sign Out
          </button>
        )}
      </div>
    </div>
  );
}
